import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Shield, Sparkles, Search, Filter } from "lucide-react";
import { toast } from "sonner";

const IMPACT_LABELS = {
  abonado: "Abonado",
  descontado: "Descontado",
  compensado: "Compensado",
  informativo: "Informativo",
  banco_horas_pago: "BH – Pago",
  banco_horas_gozado: "BH – Gozado",
  banco_horas_negativo: "BH – Negativo",
  banco_horas_positivo: "BH – Positivo",
};

const IMPACT_TONE = {
  abonado: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  descontado: "bg-red-100 text-red-800 ring-red-200",
  compensado: "bg-cyan-100 text-cyan-800 ring-cyan-200",
  informativo: "bg-slate-100 text-slate-700 ring-slate-200",
  banco_horas_pago: "bg-blue-100 text-blue-800 ring-blue-200",
  banco_horas_gozado: "bg-teal-100 text-teal-800 ring-teal-200",
  banco_horas_negativo: "bg-orange-100 text-orange-800 ring-orange-200",
  banco_horas_positivo: "bg-indigo-100 text-indigo-800 ring-indigo-200",
};

const emptyForm = {
  codigo: "", nome: "", descricao: "",
  categoria: "outros", tipo_impacto: "informativo",
  abona_falta: false, desconta_salario: false,
  entra_indice: true, entra_banco_horas: false,
  conta_dia_perdido: true, conta_hora_perdida: true,
  exige_anexo: false, cor: "#64748B", status: "ativo",
};

export default function AbsenceSituationsPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("master", "rh_admin");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [impactTypes, setImpactTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/absence-situations");
      setItems(data.items || []);
      setCategories(data.categories || []);
      setImpactTypes(data.impact_types || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (catFilter !== "all" && s.categoria !== catFilter) return false;
      if (!q) return true;
      return [s.codigo, s.nome, s.descricao, s.categoria].join(" ").toLowerCase().includes(q);
    });
  }, [items, search, statusFilter, catFilter]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...emptyForm, ...s });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.codigo.trim() || !form.nome.trim()) {
      toast.error("Informe código e nome."); return;
    }
    try {
      if (editing) {
        await api.put(`/absence-situations/${editing.id}`, form);
        toast.success("Situação atualizada");
      } else {
        await api.post("/absence-situations", form);
        toast.success("Situação criada");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleStatus = async (s) => {
    try {
      await api.put(`/absence-situations/${s.id}`, {
        ...s, status: s.status === "ativo" ? "inativo" : "ativo",
      });
      toast.success(`Situação ${s.status === "ativo" ? "inativada" : "ativada"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Inativar situação "${s.nome}"?`)) return;
    try {
      await api.delete(`/absence-situations/${s.id}`);
      toast.success("Situação inativada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/absence-situations/seed-defaults");
      toast.success(`${data.created} situações padrão criadas`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="absence-situations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Administração · Absenteísmo</div>
          <h1 className="font-display text-3xl font-semibold">Configuração de Situações</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Defina as situações de ausência usadas em importações e lançamentos manuais.
            Configure abonos, descontos, impacto no índice de absenteísmo e integração com banco de horas.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={seedDefaults} data-testid="seed-defaults-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Restaurar padrões
            </Button>
            <Button onClick={openCreate} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="new-situation-btn">
              <Plus className="h-4 w-4 mr-2" /> Nova situação
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar código, nome ou descrição..." className="pl-9"
                 data-testid="situation-search"/>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-[200px]" data-testid="cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas categorias</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="inativo">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Impacto</TableHead>
              <TableHead className="text-center">Abona</TableHead>
              <TableHead className="text-center">Desconta</TableHead>
              <TableHead className="text-center">Índice</TableHead>
              <TableHead className="text-center">Banco H.</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Nenhuma situação encontrada</TableCell></TableRow>
            ) : filtered.map((s) => (
              <TableRow key={s.id} data-testid={`sit-row-${s.id}`}>
                <TableCell className="font-mono text-[12px]">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded" style={{ background: s.cor }}></span>
                    {s.codigo}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{s.nome}</TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground capitalize">{(s.categoria || "").replace(/_/g, " ")}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`ring-1 ring-inset ${IMPACT_TONE[s.tipo_impacto] || "bg-slate-100"}`}>
                    {IMPACT_LABELS[s.tipo_impacto] || s.tipo_impacto}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">{s.abona_falta ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{s.desconta_salario ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{s.entra_indice ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{s.entra_banco_horas ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={s.status === "ativo" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-700"}>
                    {s.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(s)} title="Editar" data-testid={`edit-${s.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => toggleStatus(s)} title="Ativar/Inativar">
                          <Shield className={`h-3.5 w-3.5 ${s.status === "ativo" ? "text-emerald-700" : "text-slate-500"}`} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => del(s)} title="Inativar" data-testid={`del-${s.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Editar situação" : "Nova situação de absenteísmo"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Código</Label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                     className="font-mono uppercase" maxLength={12} data-testid="form-codigo" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="form-nome" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Categoria</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="form-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo de impacto</Label>
              <Select value={form.tipo_impacto} onValueChange={(v) => setForm({ ...form, tipo_impacto: v })}>
                <SelectTrigger data-testid="form-impacto"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {impactTypes.map((t) => <SelectItem key={t} value={t}>{IMPACT_LABELS[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Cor</Label>
              <div className="flex items-center gap-2">
                <Input type="color" value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })}
                       className="w-12 h-10 p-1" />
                <Input value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })}
                       className="font-mono" />
              </div>
            </div>

            <div className="md:col-span-3 mt-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Parâmetros de cálculo</div>
            {[
              { k: "abona_falta", l: "Abona falta", d: "Não conta como falta do colaborador" },
              { k: "desconta_salario", l: "Desconta salário", d: "Afeta folha de pagamento" },
              { k: "entra_indice", l: "Entra no índice", d: "Contabiliza no % de absenteísmo" },
              { k: "entra_banco_horas", l: "Banco de horas", d: "Afeta saldo do banco de horas" },
              { k: "conta_dia_perdido", l: "Conta dia perdido", d: "Soma no total de dias perdidos" },
              { k: "conta_hora_perdida", l: "Conta hora perdida", d: "Soma no total de horas perdidas" },
              { k: "exige_anexo", l: "Exige anexo", d: "Atestado/documento obrigatório" },
            ].map(({ k, l, d }) => (
              <label key={k} className="flex items-start gap-2 p-3 border border-border rounded-md cursor-pointer hover:bg-slate-50">
                <Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`sw-${k}`} />
                <div className="flex-1">
                  <div className="text-[12.5px] font-medium">{l}</div>
                  <div className="text-[11px] text-muted-foreground">{d}</div>
                </div>
              </label>
            ))}
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="save-situation">
              {editing ? "Salvar alterações" : "Criar situação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
